import { Modal } from "antd";
import { toast } from "react-toastify";
import { removerArquivoContexto } from "./service";

/** Extrai o "nome base" de um arquivo de contexto, removendo o prefixo de ID e o CNPJ final. */
export function extrairNomeBase(nomeArquivo) {
  const partes = nomeArquivo.split("-");
  return partes.slice(1, partes.length - 1).join("-");
}

/** Confere se algum dos `fileNames` ainda nao foi carregado (por nome, case-insensitive). */
function faltamArquivos(fileNames, requestNomeArquivo) {
  return fileNames.filter((file) => !requestNomeArquivo.some((carregado) => carregado.includes(file.toLowerCase())));
}

/**
 * "Retrato" do endereço atual da empresa — usado para comparar antes/depois
 * de pedir pra atualizar o endereço (mudança de UF), em vez de confiar
 * numa flag "já avisei" que passava mesmo se o usuário só fechasse o modal
 * sem editar nada.
 */
function extrairEndereco(dados) {
  const endereco = dados.empresa?.endereco || {};
  return {
    logradouro: endereco.logradouro || "",
    numero: endereco.numero || "",
    complemento: endereco.complemento || "",
    bairro: endereco.bairro || "",
    cidade: endereco.cidade || "",
    uf: endereco.uf || "",
    cep: endereco.cep || "",
  };
}

function enderecosIguais(a, b) {
  if (!a || !b) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * CNPJ é sempre obrigatório na Migração (tipo 3), tanto no caminho "possui
 * Inscrição Estadual" quanto no caminho "é isenta". Devolve `true` quando
 * abriu o pedido de importação (bloqueou o avanço), `false` quando o CNPJ
 * já está presente (ou o tipo não exige) e pode seguir.
 */
function precisaImportarCNPJ(ctx) {
  const { dados, docsContexto, setAlertConfig, setNomeArquivoPadrao, setModalUploadDoc } = ctx;
  if (!["1", "3", "4"].includes(dados.tipo?.id)) return false;

  const nomeArquivoBuscado = "CNPJ";
  const jaPossuiCNPJ = docsContexto?.some((arquivo) => arquivo.nome?.toLowerCase().includes(nomeArquivoBuscado.toLowerCase()));
  if (jaPossuiCNPJ) return false;

  setAlertConfig({
    open: true,
    title: "Documento Obrigatório",
    message: <><strong>{nomeArquivoBuscado}</strong> não identificado. Por favor, importe o documento!</>,
    onClose: () => {
      setNomeArquivoPadrao(nomeArquivoBuscado);
      setModalUploadDoc(true);
    },
  });
  return true;
}

/**
 * CNPJ é exigido em duas frentes, ambas obrigatórias: o arquivo/documento
 * (verificado acima) e o campo no cadastro da empresa (aba "Empresa" do
 * AtualizarCliente). Confere o arquivo primeiro; só depois de resolvido
 * checa o campo. Devolve `true` quando bloqueou o avanço.
 */
function resolverCNPJObrigatorio(ctx) {
  const { dados, setAlertConfirmAvancar, setIsModalAtualizarCliente, setOpcaoDadosEmpresa } = ctx;
  // CNPJ (arquivo + campo) é exigido em Abertura, Alteração e Migração
  // (tipos 1, 3 e 4). Tipo 5 (Baixa) tem checagem própria em
  // verificarBaixaJuntaComercial e não passa por aqui.
  if (!["1", "3", "4"].includes(dados.tipo?.id)) return false;

  if (precisaImportarCNPJ(ctx)) {
    setAlertConfirmAvancar(false);
    return true;
  }

  if (!dados.empresa?.cnpj) {
    setAlertConfirmAvancar(false);
    setIsModalAtualizarCliente(true);
    setOpcaoDadosEmpresa("empresa");
    return true;
  }

  return false;
}

/**
 * Cada verificacao recebe o mesmo `ctx` (montado uma vez por chamada de avancarOS)
 * e devolve `true` quando barrou o avanco -- nesse caso as proximas verificacoes
 * nao rodam, igual ao `return` do avancarOS original.
 */

// 0. P-DOC (tipo 3 — Migração): exige Procuracao-ECAC antes de seguir no fluxo.
function verificarProcuracaoEmPendenteDocumentacao(ctx) {
  const { dados, requestNomeArquivo, setAlertConfirmAvancar, setIsModalImportAlteracaoEnquadramento } = ctx;
  if (dados.situacao?.id !== "P-DOC" || dados.tipo?.id !== "3") return false;

  const faltando = faltamArquivos(["Procuracao-ECAC"], requestNomeArquivo);
  if (faltando.length > 0) {
    setAlertConfirmAvancar(false);
    setIsModalImportAlteracaoEnquadramento(true);
    return true;
  }
  return false;
}

// 1. V-CCM (tipo 1/3/4): exige Viabilidade da prefeitura.
function verificarViabilidadePrefeitura(ctx) {
  const { dados, requestNomeArquivo, setAlertConfig, setAlertConfirmAvancar, abrirModalImportacaoPorTipo } = ctx;
  if (dados.situacao?.id !== "V-CCM" || !["1", "3", "4"].includes(dados.tipo?.id)) return false;

  if (faltamArquivos(["Viabilidade-prefeitura"], requestNomeArquivo).length > 0) {
    setAlertConfig({
      open: true,
      title: "Atenção",
      message: <>É necessário importar a <strong>Viabilidade da prefeitura</strong></>,
    });
    setAlertConfirmAvancar(false);
    abrirModalImportacaoPorTipo(dados.tipo?.id);
    return true;
  }
  return false;
}

// 2. V-CCM (tipo 1/3/4/5): exige conselho de classe definido.
function verificarConselhoClasse(ctx) {
  const { dados, setAlertConfirmAvancar, setIsMostrarBtnConfirmacaoDeCasoSevero, naoPossuiConselho } = ctx;
  if (dados.situacao?.id !== "V-CCM" || !["1", "3", "4", "5"].includes(dados.tipo?.id)) return false;

  // "naoPossuiConselho": resposta local (não persiste no backend, vive só
  // enquanto a tela estiver aberta — mesmo mecanismo de mudancaUF/
  // mudancaMunicipio) confirmando que o usuário respondeu "Não" pra
  // "Possui conselho de classe?". Sem isso, como dados.conselho nunca é
  // gravado nesse caminho, essa checagem bloquearia pra sempre e reabriria
  // o modal em loop a cada tentativa de avançar.
  if (!naoPossuiConselho) {
    setAlertConfirmAvancar(false);
    setIsMostrarBtnConfirmacaoDeCasoSevero(true);
    return true;
  }
  return false;
}

/**
 * Pergunta se há mudança de UF e, em cascata, mudança de Município.
 * Reutilizada tanto pela checagem automática em V-CCM
 * (verificarMudancaUFMunicipioVCCM) quanto diretamente pelo modal
 * "Possui conselho de classe?" quando o usuário responde "Não" — assim a
 * pergunta de UF/Município já aparece na sequência, sem esperar um novo
 * clique em "Avançar". Cada resposta final já chama avancarDireto sozinha.
 */
export function perguntarMudancaUFMunicipioEAvancar(ctx) {
  const { setAlertConfirmAvancar, setMudancaUF, setMudancaMunicipio, avancarDireto } = ctx;
  setAlertConfirmAvancar?.(false);
  Modal.confirm({
    title: "Mudança de UF",
    content: "A alteração terá mudança de UF (estado)?",
    okText: "Sim",
    cancelText: "Não",
    centered: true,
    onOk: () => {
      setMudancaUF(true);
      avancarDireto(); // resposta final -> avança direto
    },
    onCancel: () => {
      setMudancaUF(false);
      Modal.confirm({
        title: "Mudança de Município",
        content: "A alteração terá mudança de município?",
        okText: "Sim",
        cancelText: "Não",
        centered: true,
        onOk: () => {
          setMudancaMunicipio(true);
          avancarDireto(); // resposta final -> avança direto
        },
        onCancel: () => {
          setMudancaMunicipio(false);
          avancarDireto(); // resposta final -> avança direto
        },
      });
    },
  });
}

// 2.5. V-CCM (tipo 4 — Alteração): ...
function verificarMudancaUFMunicipioVCCM(ctx) {
  const { dados, mudancaUF, naoPossuiConselho } = ctx;
  if (dados.situacao?.id !== "V-CCM" || ["3", "4"].includes(dados.tipo?.id)) return false;

  const conselhoResolvido = !(dados.conselho === null || dados.conselho === "") || naoPossuiConselho;
  if (!conselhoResolvido) return false;

  if (mudancaUF === null) {
    perguntarMudancaUFMunicipioEAvancar(ctx);
    return true;
  }

  return false;
}

//AQUI COOLOCA A FUNÇÃO DE VALIDAR SE EXISTE MUDANÇA DE UF

// 3. V-CND: exige CND Federal/Estadual/Municipal.
/*function verificarCertidoesNegativas(ctx) {
  const { dados, requestNomeArquivo, setAlertConfig, setAlertConfirmAvancar, setIsModalImportBaixaEncerramentoEmpresa } = ctx;
  if (dados.situacao?.id !== "V-CND") return false;

  const faltando = faltamArquivos(["CND-Federal", "CND-Estadual", "CND-Municipal"], requestNomeArquivo);
  if (faltando.length > 0) {
    setAlertConfig({
      open: true,
      title: "Atenção",
      message: (
        <>
          É necessário importar os documentos obrigatórios: <strong>{faltando.join(", ")}</strong>
        </>
      ),
    });
    setAlertConfirmAvancar(false);
    setIsModalImportBaixaEncerramentoEmpresa(true);
    return true;
  }
  return false;
}*/

// 4. A-RFB (tipo 1/3/4): exige FCPJ-RedeSim.
function verificarFcpsRedeSim(ctx) {
  const { dados, requestNomeArquivo, setAlertConfirmAvancar, abrirModalImportacaoPorTipo, setAlertConfig } = ctx;
  if (dados.situacao?.id !== "A-RFB" || !["1", "3", "4"].includes(dados.tipo?.id)) return false;

  const faltando = faltamArquivos(["FCPJ-RedeSim"], requestNomeArquivo);
  if (faltando.length > 0) {
  setAlertConfirmAvancar(false);
  setAlertConfig({
      open: true,
      title: "Documentos Obrigatórios Faltando",
      message: <>É necessário importar: <strong>FCPJ-RedeSim</strong></>,
      onClose: () => abrirModalImportacaoPorTipo(dados.tipo?.id),
    });
    return true;
  }
  return false;
}

// 5. A-RFB (tipo 5): exige FCN.
function verificarFCN(ctx) {
  const { dados, requestNomeArquivo, setAlertConfig, setAlertConfirmAvancar, setIsModalImportBaixaEncerramentoEmpresa } = ctx;
  if (dados.situacao?.id !== "A-RFB" || dados.tipo?.id !== "5") return false;

  const faltando = faltamArquivos(["FCN"], requestNomeArquivo);
  if (faltando.length > 0) {
    setAlertConfig({
      open: true,
      title: "Atenção",
      message: <>É necessário importar o Documento <strong>{faltando[0]}</strong></>,
    });
    setAlertConfirmAvancar(false);
    setIsModalImportBaixaEncerramentoEmpresa(true);
    return true;
  }
  return false;
}

// 6. E-JCM (tipo 1): exige modelo de Abertura da empresa.
function verificarModeloAberturaEmpresa(ctx) {
  const { dados, setNomeArquivoPadrao, verificarArquivoObrigatorio } = ctx;
  if (dados.situacao?.id !== "E-JCM" || dados.tipo?.id !== "1") return false;

  setNomeArquivoPadrao("Abertura-empresa");
  return verificarArquivoObrigatorio(["Abertura-empresa"], {
    "Abertura-empresa": <>É necessário carregar o <strong>Modelo da Abertura da empresa.</strong></>,
  });
}

// 7. E-JCM (tipo 5): exige modelo de Distrato da empresa.
function verificarModeloDistrato(ctx) {
  const { dados, EncerramentoEmpresaArquivo, setAlertConfirmAvancar, setAlertConfig, setNomeArquivoPadrao, setModalUploadDoc } = ctx;
  if (dados.situacao?.id !== "E-JCM" || dados.tipo?.id !== "5") return false;

  setAlertConfirmAvancar(false);
  if (!EncerramentoEmpresaArquivo) {
    setAlertConfig({
      open: true,
      title: "Atenção",
      message: <>Importe o <strong>Modelo de Distrato da Empresa</strong> para prosseguir.</>,
      onClose: () => {
        setNomeArquivoPadrao("Modelo-Documento-distrato");
        setModalUploadDoc(true);
      },
    });
    return true;
  }
  return false;
}

// 8. E-JCM (tipo 3/4): modelo contratual de alteracao/abertura (com renomeacao incremental).
async function verificarModeloContratoAlteracao(ctx) {
  const { dados, docsContexto, alteracaoContratualExiste, aberturaEmpresaArquivo, setAlertConfirmAvancar, setAlertConfig, setModalUploadDoc, setNomeArquivoPadrao, setFileToRemove } = ctx;
  if (dados.situacao?.id !== "E-JCM" || !["3", "4"].includes(dados.tipo?.id)) return false;

  setAlertConfirmAvancar(false);

  const modeloAltComNumeroOSDiferenteDaOSAtual = docsContexto.find((arquivo) => {
    const nomeBase = extrairNomeBase(arquivo.nome);
    if (!nomeBase.startsWith("Alteracao-contratual")) return false;
    const partes = nomeBase.split("-");
    const osArquivo = partes[partes.length - 1];
    return osArquivo !== String(dados.id);
  });

  if (modeloAltComNumeroOSDiferenteDaOSAtual) {
    setAlertConfig({
      open: true,
      title: "Atenção",
      message: <>O <strong>Modelo de Alteração de Contrato</strong> será substituido por outro.</>,
      onClose: () => setModalUploadDoc(true),
    });

    try {
      const fileName = modeloAltComNumeroOSDiferenteDaOSAtual.nome;
      await removerArquivoContexto(fileName);
      toast.success("Arquivo antigo removido. Agora importe o novo para substituição.");

      const nomeSemExtensao = fileName.replace(/\.[^/.]+$/, "");
      const match = nomeSemExtensao.match(/Alteracao-contratual-(\d{2})/);
      const contadorAtual = match ? parseInt(match[1], 10) : 0;
      const contadorNovo = String(contadorAtual + 1).padStart(2, "0");
      setNomeArquivoPadrao(`Alteracao-contratual-${contadorNovo}-${dados.id}`);
    } catch (error) {
      toast.error("Erro ao substituir arquivo.");
    }
    return true;
  }

  if (!alteracaoContratualExiste) {
    setAlertConfig({
      open: true,
      title: "Atenção",
      message: <>Importe o <strong>Modelo Contratual de Alteração</strong> para prosseguir.</>,
      onClose: () => setModalUploadDoc(true),
    });
    const contador = docsContexto.filter((arquivo) => extrairNomeBase(arquivo.nome).startsWith("Alteracao-contratual")).length + 1;
    setNomeArquivoPadrao(`Alteracao-contratual-${String(contador).padStart(2, "0")}-${dados.id}`);
    return true;
  }

  if (aberturaEmpresaArquivo) {
    setAlertConfig({
      open: true,
      title: "Atenção",
      message: (
        <>
          O <strong>Modelo Contratual de Abertura da Empresa</strong> será substituído por um{" "}
          <strong>Modelo de Alteração de Contrato</strong>.
        </>
      ),
      onClose: () => setModalUploadDoc(true),
    });
    setFileToRemove(aberturaEmpresaArquivo.nome);
    const contador = docsContexto.filter((arquivo) => extrairNomeBase(arquivo.nome).startsWith("Alteracao-contratual")).length + 1;
    setNomeArquivoPadrao(`Alteracao-contratual-${String(contador).padStart(2, "0")}-${dados.id}`);
    return true;
  }

  if (alteracaoContratualExiste) {
    await ctx.avancarDireto();
    return true;
  }
  return false;
}

// 9. A-JCM (tipo 5): CNPJ + contrato de distrato + data de encerramento.
function verificarBaixaJuntaComercial(ctx) {
  const { dados, docsContexto, requestNomeArquivo, setAlertConfig, setAlertConfirmAvancar, setIsModalImportBaixaEncerramentoEmpresa, setNomeArquivoPadrao, setModalUploadDoc, setIsModalDataEncerramento, setLoading } = ctx;
  if (dados.situacao?.id !== "A-JCM" || dados.tipo?.id !== "5") return false;

  const nomeArquivoBuscado = "Contrato-distrito";
  const faltandoCNPJ = faltamArquivos(["CNPJ"], requestNomeArquivo);
  if (faltandoCNPJ.length > 0) {
    setAlertConfig({ open: true, title: "Atenção", message: <>É necessário importar a <strong>{faltandoCNPJ[0]}</strong></> });
    setAlertConfirmAvancar(false);
    setIsModalImportBaixaEncerramentoEmpresa(true);
    return true;
  }

  const jaPossuiContrato = docsContexto?.some((arquivo) => arquivo.nome?.toLowerCase().includes(nomeArquivoBuscado.toLowerCase()));
  if (!jaPossuiContrato) {
    setAlertConfirmAvancar(false);
    setAlertConfig({
      open: true,
      title: "Documento Obrigatório",
      message: <>É necessário carregar o <strong>{nomeArquivoBuscado}</strong>.</>,
      onClose: () => {
        setNomeArquivoPadrao(nomeArquivoBuscado);
        setModalUploadDoc(true);
      },
    });
    return true;
  }

  if (!dados.empresa?.dataEncerramento) {
    setAlertConfirmAvancar(false);
    setIsModalDataEncerramento(true);
    setLoading(false);
    return true;
  }
  return false;
}

// 10. A-JCM (tipo 1/3/4/5): contrato de alteracao (tipo 3/4), documentos base
// (procuracao/termo, tipo 1/4), constituicao (tipo 1), Inscricao Estadual
// (com pergunta de isencao) e CNPJ (tipo 3 — sempre obrigatório, com ou sem IE).
function verificarAnaliseJuntaComercial(ctx) {
  const {
    dados, docsContexto, requestNomeArquivo,
    setAlertConfirmAvancar, setAlertConfig, setNomeArquivoPadrao, setModalUploadDoc,
    abrirModalImportacaoPorTipo, setIsModalAtualizarCliente, setOpcaoDadosEmpresa,
    avancarDireto, fetchData,
    setIsModalImportsAberturaAlteracaoEmpresa, setIsModalImportAlteracaoEnquadramento, setIsModalImportBaixaEncerramentoEmpresa,
    mudancaUF, setMudancaUF, setMudancaMunicipio,
    enderecoAntesDaMudancaUF, setEnderecoAntesDaMudancaUF,
  } = ctx;
  if (dados.situacao?.id !== "A-JCM" || !["3", "5"].includes(dados.tipo?.id)) return false;
  // tipo 1 (Abertura) e tipo 4 (Alteração) têm checagem própria, ver
  // verificarAnaliseJuntaComercialAbertura e verificarAnaliseJuntaComercialAlteracao.

  if (dados.tipo?.id === "3") {
    setAlertConfirmAvancar(false);

    // 0. Mudança de UF (mesma funcionalidade já aplicada na Alteração,
    // tipo 4): pergunta se a Migração muda de UF e, em caso afirmativo,
    // exige o par Constituicao-Contrato-Social-Origem/Destino, forçando
    // depois a atualização do ENDEREÇO da empresa no cadastro (mesmo
    // mecanismo de "retrato antes/depois" usado na Alteração, pra garantir
    // edição de verdade e não só o fechar do modal).
    if (mudancaUF === null) {
      setAlertConfirmAvancar(false);
      Modal.confirm({
        title: "Mudança de UF",
        content: "A migração terá mudança de UF (estado)?",
        okText: "Sim",
        cancelText: "Não",
        centered: true,
        onOk: () => setMudancaUF(true),
        onCancel: () => {
          setMudancaUF(false);
          Modal.confirm({
            title: "Mudança de Município",
            content: "A migração terá mudança de município?",
            okText: "Sim",
            cancelText: "Não",
            centered: true,
            onOk: () => setMudancaMunicipio(true),
            onCancel: () => setMudancaMunicipio(false),
          });
        },
      });
      return true;
    }

    if (mudancaUF) {
      const temOrigem = docsContexto?.some((arquivo) =>
        arquivo.nome?.toLowerCase().includes("constituicao-contrato-social-origem")
      );
      if (!temOrigem) {
        setAlertConfirmAvancar(false);
        setAlertConfig({
          open: true,
          title: "Documento Obrigatório",
          message: <>É necessário carregar o <strong>Constituicao-Contrato-Social-Origem</strong>.</>,
          onClose: () => {
            setNomeArquivoPadrao("Constituicao-Contrato-Social-Origem");
            setModalUploadDoc(true);
          },
        });
        return true;
      }

      const temDestino = docsContexto?.some((arquivo) =>
        arquivo.nome?.toLowerCase().includes("constituicao-contrato-social-destino")
      );
      if (!temDestino) {
        setAlertConfirmAvancar(false);
        setAlertConfig({
          open: true,
          title: "Documento Obrigatório",
          message: <>É necessário carregar o <strong>Constituicao-Contrato-Social-Destino</strong>.</>,
          onClose: () => {
            setNomeArquivoPadrao("Constituicao-Contrato-Social-Destino");
            setModalUploadDoc(true);
          },
        });
        return true;
      }

      const enderecoAtual = extrairEndereco(dados);
      if (!enderecoAntesDaMudancaUF) {
        setAlertConfirmAvancar(false);
        toast.info(
          "IMPORTANTE: a empresa mudou de UF. Atualize o ENDEREÇO da empresa no cadastro antes de prosseguir."
        );
        setEnderecoAntesDaMudancaUF(enderecoAtual);
        setIsModalAtualizarCliente(true);
        setOpcaoDadosEmpresa("endereco");
        return true;
      }

      if (enderecosIguais(enderecoAtual, enderecoAntesDaMudancaUF)) {
        setAlertConfirmAvancar(false);
        toast.warning(
          "O endereço ainda não foi alterado. Como a empresa mudou de UF, é necessário atualizar o endereço da empresa no cadastro antes de prosseguir."
        );
        setIsModalAtualizarCliente(true);
        setOpcaoDadosEmpresa("endereco");
        return true;
      }
    }

    // 1. Contrato de Alteração (CIT) — checklist já existente da Migração.
    const arquivosAlteracao = docsContexto.filter((arquivo) => extrairNomeBase(arquivo.nome).startsWith("Contrato-alteracao"));
    const existeArquivoOSAtual = arquivosAlteracao.some((arquivo) => {
      const partes = extrairNomeBase(arquivo.nome).split("-");
      return partes[partes.length - 1] === String(dados.id);
    });

    if (!existeArquivoOSAtual) {
      const contadorNovo = String(arquivosAlteracao.length + 1).padStart(2, "0");
      const novoNome = `Contrato-alteracao-${contadorNovo}-${dados.id}`;
      setAlertConfig({
        open: true,
        title: "Documento Obrigatório",
        message: <>Para prosseguir, é necessário importar o <strong>Contrato de Alteração (CIT)</strong> referente á esta Ordem.</>,
        onClose: () => {
          setNomeArquivoPadrao(novoNome);
          setModalUploadDoc(true);
        },
      });
      return true;
    }
  }

  // Inscrição Estadual + CNPJ.
  return verificarInscricaoEstadualECNPJ(ctx);
}

// 10a. A-JCM (tipo 1 — Abertura): documentos um de cada vez, na ordem
// Constituição → CNPJ (arquivo) → Procuração ECAC → Termo de Regime
// Tributário → Inscrição Estadual. No final, se faltar o campo CNPJ e/ou
// IE no cadastro, abre o AtualizarCliente pra preencher os dois.
function verificarAnaliseJuntaComercialAbertura(ctx) {
  const {
    dados, docsContexto, requestNomeArquivo,
    setAlertConfirmAvancar, setAlertConfig, setNomeArquivoPadrao, setModalUploadDoc,
    setIsModalImportsAberturaAlteracaoEmpresa, setIsModalAtualizarCliente, setOpcaoDadosEmpresa,
    fetchData,
  } = ctx;
  if (dados.situacao?.id !== "A-JCM" || dados.tipo?.id !== "1") return false;

  // 1. Constituição de Contrato Social
  const nomeConstituicao = "Constituicao-Contrato-Social";
  const temConstituicao = docsContexto?.some((arquivo) => arquivo.nome?.toLowerCase().includes(nomeConstituicao.toLowerCase()));
  if (!temConstituicao) {
    setAlertConfirmAvancar(false);
    setAlertConfig({
      open: true,
      title: "Documento Obrigatório",
      message: <>É necessário carregar o <strong>{nomeConstituicao}</strong>.</>,
      onClose: () => {
        setNomeArquivoPadrao(nomeConstituicao);
        setModalUploadDoc(true);
      },
    });
    return true;
  }

  // 2. CNPJ (arquivo)
  if (precisaImportarCNPJ(ctx)) {
    setAlertConfirmAvancar(false);
    return true;
  }

  // 3. Procuração ECAC
  if (faltamArquivos(["Procuracao-ECAC"], requestNomeArquivo).length > 0) {
    setAlertConfirmAvancar(false);
    setAlertConfig({
      open: true,
      title: "Documentos Obrigatórios Faltando",
      message: <>É necessário importar: <strong>Procuracao-ECAC</strong></>,
      onClose: () => setIsModalImportsAberturaAlteracaoEmpresa(true),
    });
    return true;
  }

  // 4. Termo de Regime Tributário
  if (faltamArquivos(["Termo-de-regime-tributario"], requestNomeArquivo).length > 0) {
    setAlertConfirmAvancar(false);
    setAlertConfig({
      open: true,
      title: "Documentos Obrigatórios Faltando",
      message: <>É necessário importar: <strong>Termo-de-regime-tributario</strong></>,
      onClose: () => setIsModalImportsAberturaAlteracaoEmpresa(true),
    });
    return true;
  }

  // 5. Inscrição Estadual — documento + pergunta de isenção (mesmo padrão
  // usado em Migração, mas só com o efeito colateral de abrir o import de
  // Abertura, já que aqui o tipo é sempre "1").
  const temArquivoInscricao = requestNomeArquivo.some((nome) => nome.includes("inscricao-estadual"));
  const declaradaIsenta = ["N", "I"].includes(dados.empresa?.contribuicaoIe);

  if (!temArquivoInscricao && !declaradaIsenta) {
    setAlertConfirmAvancar(false);
    Modal.confirm({
      title: "Inscrição Estadual",
      content: "Identificamos que a empresa não possui o documento de Inscrição Estadual. A empresa possui IE?",
      okText: "Sim, possui",
      cancelText: "Não, é isenta",
      centered: true,
      onOk: () => {
        toast.info("Primeiro, realize o upload do documento de Inscrição.");
        setNomeArquivoPadrao("Inscricao-estadual");
        fetchData().then(() => setIsModalImportsAberturaAlteracaoEmpresa(true));
      },
      onCancel: () => {
        if (dados.empresa?.ie) {
          setAlertConfirmAvancar(false);
          Modal.warning({
            title: "Conflito de Informação",
            content: (
              <>
                Identificamos que existe um número de IE cadastrado (<strong>{dados.empresa.ie}</strong>). Para marcar
                como isenta, você deve primeiro remover o número no cadastro da empresa.
              </>
            ),
            okText: "Corrigir Agora",
            onOk: () => {
              setIsModalAtualizarCliente(true);
              setOpcaoDadosEmpresa("dadosEmpresa");
            },
          });
          return;
        }

        toast.info('Marque a empresa como isenta na aba "Dados da Empresa" e salve para confirmar.');
        setIsModalAtualizarCliente(true);
        setOpcaoDadosEmpresa("dadosEmpresa");
      },
    });
    return true;
  }

  // 6. Campos no cadastro: CNPJ e/ou IE, juntos no mesmo modal ao final.
  const faltaCampoIE = !declaradaIsenta && !dados.empresa?.ie;
  const faltaCampoCNPJ = !dados.empresa?.cnpj;
  if (faltaCampoIE || faltaCampoCNPJ) {
    setAlertConfirmAvancar(false);
    setIsModalAtualizarCliente(true);
    setOpcaoDadosEmpresa(faltaCampoIE ? "dadosEmpresa" : "empresa");
    return true;
  }

  return false;
}

// 10b. A-JCM (tipo 4 — Alteração): mesma sequência da Abertura, um item por
// vez, MAS sem exigir Termo-de-regime-tributario. No final, o modal do
// cadastro cobre CNPJ, NIRE e IE juntos (Alteração também exige NIRE).
function verificarAnaliseJuntaComercialAlteracao(ctx) {
  const {
    dados, docsContexto, requestNomeArquivo,
    setAlertConfirmAvancar, setAlertConfig, setNomeArquivoPadrao, setModalUploadDoc,
    setIsModalImportsAberturaAlteracaoEmpresa, setIsModalAtualizarCliente, setOpcaoDadosEmpresa,
    fetchData,
    mudancaUF, setMudancaUF, setMudancaMunicipio,
    enderecoAntesDaMudancaUF, setEnderecoAntesDaMudancaUF,
  } = ctx;
  if (dados.situacao?.id !== "A-JCM" || dados.tipo?.id !== "4") return false;

  // A resposta de "Mudança de UF" fica guardada em estado (mudancaUF) e só
  // volta a ser null quando:
  //   (a) a OS nunca passou por essa pergunta ainda, ou
  //   (b) o usuário excluiu explicitamente o(s) documento(s) amarrado(s) à
  //       resposta anterior, pelo botão "Remover" na tabela de documentos
  //       (ver TabelaContextDoc — confirmarExclusao). O reset acontece lá,
  //       no momento da exclusão, e NÃO é inferido aqui a partir de
  //       "passouPorAJCM" — isso é o que causava o loop: um flag que
  //       permanecia true forçava o reset a cada clique em "Avançar",
  //       mesmo que o usuário tivesse acabado de responder e ainda não
  //       tivesse tido chance de subir o documento.

  // 0. Primeira coisa ao chegar em "Análise Junta Comercial": pergunta se
  // a alteração muda de UF. Se não, pergunta se muda de Município. A
  // resposta fica guardada em estado (React, não persiste se a página
  // recarregar) e decide se a Constituição/Inscrição Municipal vão pedir
  // os documentos normais ou o par Destino+Origem.
  //
  // OBS: se a OS já passou pela pergunta em V-CCM (verificarMudancaUFMunicipioVCCM),
  // mudancaUF já não é mais null e este bloco é pulado automaticamente.
  if (mudancaUF === null) {
    setAlertConfirmAvancar(false);
    Modal.confirm({
      title: "Mudança de UF",
      content: "A alteração terá mudança de UF (estado)?",
      okText: "Sim",
      cancelText: "Não",
      centered: true,
      onOk: () => setMudancaUF(true),
      onCancel: () => {
        setMudancaUF(false);
        Modal.confirm({
          title: "Mudança de Município",
          content: "A alteração terá mudança de município?",
          okText: "Sim",
          cancelText: "Não",
          centered: true,
          onOk: () => setMudancaMunicipio(true),
          onCancel: () => setMudancaMunicipio(false),
        });
      },
    });
    return true;
  }

  // 1. Constituição de Contrato Social. Com mudança de UF, o documento
  // único vira dois — primeiro o de Destino, depois o de Origem — e ao
  // concluir os dois força a atualização do ENDEREÇO da empresa no
  // cadastro (uma vez só; "enderecoAtualizadoAposMudancaUF" evita repetir
  // esse aviso a cada clique em Avançar depois que ele já foi mostrado).
  if (mudancaUF) {
    const temOrigem = docsContexto?.some((arquivo) =>
      arquivo.nome?.toLowerCase().includes("constituicao-contrato-social-origem")
    );
    if (!temOrigem) {
      setAlertConfirmAvancar(false);
      setAlertConfig({
        open: true,
        title: "Documento Obrigatório",
        message: <>É necessário carregar o <strong>Constituicao-Contrato-Social-Origem</strong>.</>,
        onClose: () => {
          setNomeArquivoPadrao("Constituicao-Contrato-Social-Origem");
          setModalUploadDoc(true);
        },
      });
      return true;
    }


    const temDestino = docsContexto?.some((arquivo) =>
      arquivo.nome?.toLowerCase().includes("constituicao-contrato-social-destino")
    );
    if (!temDestino) {
      setAlertConfirmAvancar(false);
      setAlertConfig({
        open: true,
        title: "Documento Obrigatório",
        message: <>É necessário carregar o <strong>Constituicao-Contrato-Social-Destino</strong>.</>,
        onClose: () => {
          setNomeArquivoPadrao("Constituicao-Contrato-Social-Destino");
          setModalUploadDoc(true);
        },
      });
      return true;
    }

    

    // Em vez de só "avisar uma vez", guarda um retrato do endereço no
    // momento em que os dois documentos ficam prontos, e só libera quando
    // o endereço atual for DIFERENTE desse retrato — ou seja, quando o
    // usuário realmente tiver editado algo, não só fechado o modal.
    const enderecoAtual = extrairEndereco(dados);
    if (!enderecoAntesDaMudancaUF) {
      setAlertConfirmAvancar(false);
      toast.info(
        "IMPORTANTE: a empresa mudou de UF. Atualize o ENDEREÇO da empresa no cadastro antes de prosseguir."
      );
      setEnderecoAntesDaMudancaUF(enderecoAtual);
      setIsModalAtualizarCliente(true);
      setOpcaoDadosEmpresa("endereco");
      return true;
    }

    if (enderecosIguais(enderecoAtual, enderecoAntesDaMudancaUF)) {
      setAlertConfirmAvancar(false);
      toast.warning(
        "O endereço ainda não foi alterado. Como a empresa mudou de UF, é necessário atualizar o endereço da empresa no cadastro antes de prosseguir."
      );
      setIsModalAtualizarCliente(true);
      setOpcaoDadosEmpresa("endereco");
      return true;
    }
  } else {
    const nomeConstituicao = "Constituicao-Contrato-Social";
    const temConstituicao = docsContexto?.some((arquivo) => arquivo.nome?.toLowerCase().includes(nomeConstituicao.toLowerCase()));
    if (!temConstituicao) {
      setAlertConfirmAvancar(false);
      setAlertConfig({
        open: true,
        title: "Documento Obrigatório",
        message: <>É necessário carregar o <strong>{nomeConstituicao}</strong>.</>,
        onClose: () => {
          setNomeArquivoPadrao(nomeConstituicao);
          setModalUploadDoc(true);
        },
      });
      return true;
    }
  }

  // 2. CNPJ (arquivo)
  if (precisaImportarCNPJ(ctx)) {
    setAlertConfirmAvancar(false);
    return true;
  }

  // 3. Procuração ECAC (aqui NÃO pede Termo-de-regime-tributario)
  if (faltamArquivos(["Procuracao-ECAC"], requestNomeArquivo).length > 0) {
    setAlertConfirmAvancar(false);
    setAlertConfig({
      open: true,
      title: "Documentos Obrigatórios Faltando",
      message: <>É necessário importar: <strong>Procuracao-ECAC</strong></>,
      onClose: () => setIsModalImportsAberturaAlteracaoEmpresa(true),
    });
    return true;
  }

  // 4. Inscrição Estadual — documento + pergunta de isenção.
  const temArquivoInscricao = requestNomeArquivo.some((nome) => nome.includes("inscricao-estadual"));
  const declaradaIsenta = ["N", "I"].includes(dados.empresa?.contribuicaoIe);

  if (!temArquivoInscricao && !declaradaIsenta) {
    setAlertConfirmAvancar(false);
    Modal.confirm({
      title: "Inscrição Estadual",
      content: "Identificamos que a empresa não possui o documento de Inscrição Estadual. A empresa possui IE?",
      okText: "Sim, possui",
      cancelText: "Não, é isenta",
      centered: true,
      onOk: () => {
        toast.info("Primeiro, realize o upload do documento de Inscrição.");
        setNomeArquivoPadrao("Inscricao-estadual");
        fetchData().then(() => setIsModalImportsAberturaAlteracaoEmpresa(true));
      },
      onCancel: () => {
        if (dados.empresa?.ie) {
          setAlertConfirmAvancar(false);
          Modal.warning({
            title: "Conflito de Informação",
            content: (
              <>
                Identificamos que existe um número de IE cadastrado (<strong>{dados.empresa.ie}</strong>). Para marcar
                como isenta, você deve primeiro remover o número no cadastro da empresa.
              </>
            ),
            okText: "Corrigir Agora",
            onOk: () => {
              setIsModalAtualizarCliente(true);
              setOpcaoDadosEmpresa("dadosEmpresa");
            },
          });
          return;
        }

        toast.info('Marque a empresa como isenta na aba "Dados da Empresa" e salve para confirmar.');
        setIsModalAtualizarCliente(true);
        setOpcaoDadosEmpresa("dadosEmpresa");
      },
    });
    return true;
  }

  // 5. Campos no cadastro: CNPJ, NIRE e/ou IE, juntos no mesmo modal ao final.
  const faltaCampoIE = !declaradaIsenta && !dados.empresa?.ie;
  const faltaCampoNire = !dados.empresa?.nire;
  const faltaCampoCNPJ = !dados.empresa?.cnpj;
  if (faltaCampoIE || faltaCampoNire || faltaCampoCNPJ) {
    setAlertConfirmAvancar(false);
    setIsModalAtualizarCliente(true);
    setOpcaoDadosEmpresa(faltaCampoIE || faltaCampoNire ? "dadosEmpresa" : "empresa");
    return true;
  }

  return false;
}

/**
 * Pergunta se a empresa possui Inscrição Estadual e, dependendo da resposta,
 * exige a importação do documento (+ CNPJ) ou marca como isenta (+ CNPJ).
 * Reutilizada em mais de uma situação (ver VERIFICACOES_AVANCO) — não checa
 * situação/tipo sozinha, isso é responsabilidade de quem a chama.
 */
function verificarInscricaoEstadualECNPJ(ctx) {
  const {
    dados, requestNomeArquivo,
    setAlertConfirmAvancar, setIsModalAtualizarCliente, setOpcaoDadosEmpresa,
    fetchData, setNomeArquivoPadrao,
    setIsModalImportsAberturaAlteracaoEmpresa, setIsModalImportAlteracaoEnquadramento, setIsModalImportBaixaEncerramentoEmpresa,
  } = ctx;

  const temArquivoInscricao = requestNomeArquivo.some((nome) => nome.includes("inscricao-estadual"));
  // "Contribuinte de ICMS" já gravado no cadastro como Não-contribuinte (N)
  // ou Isento (I) — é o único jeito PERSISTIDO de saber que a resposta
  // "é isenta" já foi dada antes. Sem checar isso, a pergunta reaparecia a
  // cada clique em "Avançar" enquanto o CNPJ ainda estivesse pendente,
  // porque nada além do documento (que isenta nunca tem) era conferido.
  const declaradaIsenta = ["N", "I"].includes(dados.empresa?.contribuicaoIe);

  if (!temArquivoInscricao && !declaradaIsenta) {
    setAlertConfirmAvancar(false);
    Modal.confirm({
      title: "Inscrição Estadual",
      content: "Identificamos que a empresa não possui o documento de Inscrição Estadual. A empresa possui IE?",
      okText: "Sim, possui",
      cancelText: "Não, é isenta",
      centered: true,
      onOk: () => {
        toast.info("Primeiro, realize o upload do documento de Inscrição.");
        setNomeArquivoPadrao("Inscricao-estadual");
        fetchData().then(() => {
          if (dados.tipo?.id === "5") setIsModalImportBaixaEncerramentoEmpresa(true);
          else if (dados.tipo?.id === "3") setIsModalImportAlteracaoEnquadramento(true);
          else if (dados.tipo?.id === "1") setIsModalImportsAberturaAlteracaoEmpresa(true);
        });
      },
      onCancel: () => {
        if (dados.empresa?.ie) {
          setAlertConfirmAvancar(false);
          Modal.warning({
            title: "Conflito de Informação",
            content: (
              <>
                Identificamos que existe um número de IE cadastrado (<strong>{dados.empresa.ie}</strong>). Para marcar
                como isenta, você deve primeiro remover o número no cadastro da empresa.
              </>
            ),
            okText: "Corrigir Agora",
            onOk: () => {
              setIsModalAtualizarCliente(true);
              setOpcaoDadosEmpresa("dadosEmpresa");
            },
          });
          return;
        }

        // Precisa gravar a isenção no cadastro (aba "Dados da Empresa", campo
        // "Contribuinte de ICMS" = Não-contribuinte ou Isento) — sem salvar
        // isso, a pergunta reaparece no próximo "Avançar".
        toast.info('Marque a empresa como isenta na aba "Dados da Empresa" e salve para confirmar.');
        setIsModalAtualizarCliente(true);
        setOpcaoDadosEmpresa("dadosEmpresa");
      },
    });
    return true;
  }

  if (!temArquivoInscricao && declaradaIsenta) {
    // Isenção já confirmada e salva no cadastro — só falta o CNPJ (se exigido).
    if (resolverCNPJObrigatorio(ctx)) return true;
    return false;
  }

  // Documento de IE presente — falta preencher o número da IE no cadastro?
  if (!dados.empresa?.ie) {
    toast.info("Documento da Inscrição Estadual identificado. Agora, atualize o número da IE no cadastro.");
    setIsModalAtualizarCliente(true);
    setOpcaoDadosEmpresa("dadosEmpresa");
    return true;
  }

  if (resolverCNPJObrigatorio(ctx)) return true;

  return false;
}

// 11. A-CCM (tipo 1/3/4/5): exige guia-tfe + inscricao-municipal.
function verificarDocumentosCCM(ctx) {
const {
    dados, docsContexto, requestNomeArquivo, setAlertConfig, setAlertConfirmAvancar, setNomeArquivoPadrao,
    setModalUploadDoc, abrirModalImportacaoPorTipo, setIsModalAtualizarCliente, setOpcaoDadosEmpresa,
    mudancaUF, mudancaMunicipio, setMudancaMunicipio, imAntesDaMudanca, setImAntesDaMudanca,
  } = ctx;
  if (dados.situacao?.id !== "A-CCM" || !["1", "3", "4", "5"].includes(dados.tipo?.id)) return false;


   // Mudança de Município: pode ficar pendente ao chegar em "Análise CCM"
   // tanto na Alteração (tipo 4) quanto na Migração (tipo 3) — nos dois
   // casos a pergunta de "Mudança de UF" já foi feita antes (em
   // verificarAnaliseJuntaComercialAlteracao / verificarAnaliseJuntaComercial,
   // ou já em V-CCM via verificarMudancaUFMunicipioVCCM pra tipo 4), e se
   // a resposta foi "Não", a de Município ainda pode não ter sido
   // respondida (ex.: OS que chega direto nessa situação).
   if (["3", "4"].includes(dados.tipo?.id) && !mudancaUF && mudancaMunicipio === null) {
    setAlertConfirmAvancar(false);
    Modal.confirm({
      title: "Mudança de Município",
      content: `A ${dados.tipo?.id === "4" ? "alteração" : "migração"} terá mudança de município?`,
      okText: "Sim",
      cancelText: "Não",
      centered: true,
      onOk: () => setMudancaMunicipio(true),
      onCancel: () => setMudancaMunicipio(false),
    });
    return true;
  }


  // Alteração (tipo 4) e Migração (tipo 3), quando há mudança de UF ou de
  // Município, trocam o fluxo da inscrição municipal para o par
  // Destino+Origem. guia-tfe continua sendo pedido normalmente, independente
  // disso.
  const precisaOrigemDestino = ["3", "4"].includes(dados.tipo?.id) && (mudancaUF || mudancaMunicipio);

  if (precisaOrigemDestino) {
    const temDestino = docsContexto?.some((arquivo) => arquivo.nome?.toLowerCase().includes("inscricao-municipal-destino"));
    if (!temDestino) {
      setAlertConfirmAvancar(false);
      setAlertConfig({
        open: true,
        title: "Documento Obrigatório",
        message: <>É necessário carregar o <strong>inscricao-municipal-destino</strong>.</>,
        onClose: () => {
          setNomeArquivoPadrao("inscricao-municipal-destino");
          setModalUploadDoc(true);
        },
      });
      return true;
    }

    const temOrigem = docsContexto?.some((arquivo) => arquivo.nome?.toLowerCase().includes("inscricao-municipal-origem"));
    if (!temOrigem) {
      setAlertConfirmAvancar(false);
      setAlertConfig({
        open: true,
        title: "Documento Obrigatório",
        message: <>É necessário carregar o <strong>inscricao-municipal-origem</strong>.</>,
        onClose: () => {
          setNomeArquivoPadrao("inscricao-municipal-origem");
          setModalUploadDoc(true);
        },
      });
      return true;
    }
  } else if (dados.tipo?.id === "4") {
    // Alteração sem mudança de UF/Município: inscricao-municipal deixa de
    // fazer parte do checklist guiado do fluxoDocumentos — vira um
    // documento avulso, no mesmo mecanismo do Destino/Origem (docsContexto
    // + upload simples via ImportDocumentos/setModalUploadDoc).
    const temInscricaoMunicipal = docsContexto?.some((arquivo) => arquivo.nome?.toLowerCase().includes("inscricao-municipal"));
    if (!temInscricaoMunicipal) {
      setAlertConfirmAvancar(false);
      setAlertConfig({
        open: true,
        title: "Documento Obrigatório",
        message: <>É necessário carregar o <strong>inscricao-municipal</strong>.</>,
        onClose: () => {
          setNomeArquivoPadrao("inscricao-municipal");
          setModalUploadDoc(true);
        },
      });
      return true;
    }
  } else if (faltamArquivos(["inscricao-municipal"], requestNomeArquivo).length > 0) {
    // tipos 1/3/5 sem mudança de UF/Município: continuam pelo checklist
    // guiado normal (comportamento original preservado).
    setAlertConfig({
      open: true,
      title: "Atenção",
      message: <>É necessário importar os documentos obrigatórios: <strong>inscricao-municipal</strong></>,
    });
    setAlertConfirmAvancar(false);
    setNomeArquivoPadrao("inscricao-municipal");
    abrirModalImportacaoPorTipo(dados.tipo?.id);
    return true;
  }

  const temguiatfe = docsContexto?.some((arquivo) => arquivo.nome?.toLowerCase().includes("guia-tfe"));
  if (!temguiatfe) {
    setAlertConfirmAvancar(false);
    setAlertConfig({
      open: true,
      title: "Atenção",
      message: <>É necessário importar a <strong>guia-tfe</strong></>,
      onClose: () => {
        setNomeArquivoPadrao("guia-tfe");
        setModalUploadDoc(true);
      }
    });
    return true;
  }

  // Com Destino+Origem importados, guarda um retrato do valor atual do IM
  // (na primeira vez) e só libera quando o IM atual for DIFERENTE desse
  // retrato — força a edição de verdade, não só fechar o modal.
  if (precisaOrigemDestino) {
    const imAtual = dados.empresa?.im || "";

    if (imAntesDaMudanca === null) {
      setAlertConfirmAvancar(false);
      toast.info(
        "IMPORTANTE: foi importado um documento de Inscrição Municipal de origem. Atualize o número da IM no cadastro, mesmo que já exista um valor preenchido."
      );
      setImAntesDaMudanca(imAtual);
      setIsModalAtualizarCliente(true);
      setOpcaoDadosEmpresa("dadosEmpresa");
      return true;
    }

    if (imAtual === imAntesDaMudanca) {
      setAlertConfirmAvancar(false);
      toast.warning(
        "O campo IM ainda não foi alterado. Como foi importado um documento de Inscrição Municipal de origem, é necessário atualizar o número da IM no cadastro antes de prosseguir."
      );
      setIsModalAtualizarCliente(true);
      setOpcaoDadosEmpresa("dadosEmpresa");
      return true;
    }
  }

  return false;
}

// 11.5. A-CCM (tipo 3 — Migração): depois dos arquivos (guia-tfe +
// inscricao-municipal), exige o campo Inscrição Municipal (IM) preenchido
// no cadastro — mesmo padrão usado pra IE: arquivo primeiro, campo depois.
function verificarCampoIMVazio(ctx) {
  const { dados, requestNomeArquivo, setAlertConfirmAvancar, setAlertConfig, setIsModalAtualizarCliente, setOpcaoDadosEmpresa, setLoading } = ctx;
  if (dados.situacao?.id !== "A-CCM" || !["3", "4"].includes(dados.tipo?.id)) return false;

  const temArquivoInscricaoMunicipal = requestNomeArquivo.some((nome) => nome.includes("inscricao-municipal"));

  if (temArquivoInscricaoMunicipal && !dados.empresa?.im) {
    setAlertConfirmAvancar(false);
    setAlertConfig({
      open: true,
      title: "Atenção",
      message: <>Documento de <strong>Inscrição Municipal</strong> identificado. Agora, atualize o número da IM no cadastro.</>,
    });
    setIsModalAtualizarCliente(true);
    setOpcaoDadosEmpresa("dadosEmpresa");
    setLoading(false);
    return true;
  }
  return false;
}

// 12. E-RT: apenas sugere revisar o cadastro do cliente (nao bloqueia o avanco).
function verificarCadastroEmRegimeTributario(ctx) {
  const { dados, setAlertConfirmAvancar, setAlertConfig, setIsModalAtualizarCliente } = ctx;
  if (dados.situacao?.id !== "E-RT" || !["1", "3", "4", "5"].includes(dados.tipo?.id)) return false;

  setAlertConfirmAvancar(false);
  setAlertConfig({
    open: true,
    title: "Atenção",
    message: <>Verifica se é necessário fazer <strong>atualizações no Cadastro do cliente</strong>!</>,
  });
  setIsModalAtualizarCliente(true);
  return false; // nao interrompe o fluxo, igual ao original
}

// 13. P: exige cliente cadastrado.
function verificarClienteCadastrado(ctx) {
  const { dados, clienteExists, setAlertConfirmAvancar, setModoCadastro, setIsModalCadastrarCliente, setLoading } = ctx;
  if (dados.situacao?.id !== "P" || clienteExists) return false;

  setAlertConfirmAvancar(false);
  setModoCadastro("optionCadastrarCliente");
  setIsModalCadastrarCliente(true);
  setLoading(false);
  return true;
}

// 14. Qualquer situacao: exige analista atribuido.
function verificarAnalistaAtribuido(ctx) {
  const { dados, setAlertConfirmAvancar, setIsModalAtribuirContador, setLoading } = ctx;
  if (dados.usuarioAtribuicao?.id !== null && dados.usuarioAtribuicao?.id !== "") return false;

  setAlertConfirmAvancar(false);
  setIsModalAtribuirContador(true);
  setLoading(false);
  return true;
}

// 15. M-TE (tipo 3): exige data de migracao + Desenquadramento-mei.
function verificarDesenquadramentoMei(ctx) {
  const { dados, requestNomeArquivo, setAlertConfirmAvancar, setIsModalDataMigracao, setLoading, setAlertConfig, setIsModalImportAlteracaoEnquadramento } = ctx;
  if (dados.situacao?.id !== "M-TE" || dados.tipo?.id !== "3") return false;

  if (!dados.empresa?.dataMigracao) {
    setAlertConfirmAvancar(false);
    setIsModalDataMigracao(true);
    setLoading(false);
    return true;
  }

  const faltando = faltamArquivos(["Desenquadramento-mei"], requestNomeArquivo);
  if (faltando.length > 0) {
    setAlertConfig({
      open: true,
      title: "Atenção",
      message: <>É necessário importar: <strong>{faltando.join(", ")}</strong></>,
    });
    setAlertConfirmAvancar(false);
    setIsModalImportAlteracaoEnquadramento(true);
    return true;
  }
  return false;
}

// 16. C-CCM: exige protocolo do conselho de classe, quando a OS tem conselho vinculado.
function verificarProtocoloConselhoClasse(ctx) {
  const { dados, docsContexto, setAlertConfirmAvancar, setAlertConfig, setNomeArquivoPadrao, setModalUploadDoc } = ctx;
  if (dados.situacao?.id !== "C-CCM" || !["1", "3", "4", "5"].includes(dados.tipo?.id)) return false;
  if (dados.conselho === null) return false;

  const nomeArquivoBuscado = "Documento-certidao-conselho-classe";
  const jaPossuiContrato = docsContexto?.some((arquivo) => arquivo.nome?.toLowerCase().includes(nomeArquivoBuscado.toLowerCase()));
  if (!jaPossuiContrato) {
    setAlertConfirmAvancar(false);
    setAlertConfig({
      open: true,
      title: "Documento Obrigatório",
      message: <><strong>{nomeArquivoBuscado}</strong> não identificado. Por favor, importe o documento!</>,
      onClose: () => {
        setNomeArquivoPadrao(nomeArquivoBuscado);
        setModalUploadDoc(true);
      },
    });
    return true;
  }
  return false;
}

/**
 * Verificacoes executadas em sequencia, na mesma ordem do `avancarOS` original.
 * Algumas sao assincronas (podem chamar a API); todas recebem o mesmo `ctx`.
 */
export const VERIFICACOES_AVANCO = [
  verificarProcuracaoEmPendenteDocumentacao,
  verificarViabilidadePrefeitura,
  verificarConselhoClasse,
  verificarMudancaUFMunicipioVCCM,
  //verificarCertidoesNegativas,
  verificarFcpsRedeSim,
  verificarFCN,
  verificarModeloAberturaEmpresa,
  verificarModeloDistrato,
  verificarModeloContratoAlteracao,
  verificarBaixaJuntaComercial,
  verificarAnaliseJuntaComercialAbertura,
  verificarAnaliseJuntaComercialAlteracao,
  verificarAnaliseJuntaComercial,
  verificarDocumentosCCM,
  verificarCampoIMVazio,
  verificarCadastroEmRegimeTributario,
  verificarClienteCadastrado,
  verificarAnalistaAtribuido,
  verificarDesenquadramentoMei,
  verificarProtocoloConselhoClasse,
];

export { faltamArquivos };